import React, { useState, useEffect } from 'react';
import { Link } from 'react-scroll';
import { Menu, X } from 'lucide-react';
import HeroSection from './components/HeroSection';
import { Helmet } from 'react-helmet-async';

function App() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const [activeFaqIndex, setActiveFaqIndex] = useState(null);

  const [testimonialIndex, setTestimonialIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setTestimonialIndex((prev) => (prev + 1) % 5);
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  const toggleMobileMenu = () => setMobileMenuOpen(!mobileMenuOpen);

  const toggleFAQ = (index) =>
    setActiveFaqIndex(activeFaqIndex === index ? null : index);
return (
  <>
    <Helmet>
     <script type="application/ld+json">
  {JSON.stringify({
    "@context": "https://schema.org",
    "@type": "MedicalClinic",
    "name": "Prana Homoeopathic Clinic",
    "url": "https://www.pranahomeopathy.com",
    "image": "https://www.pranahomeopathy.com/logo192.png",
    "telephone": "+91-9631417625",
    "email": "homeopathyprana@gmail.com",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Near Aruni Public School, Deepatoli",
      "addressLocality": "Ranchi",
      "addressRegion": "Jharkhand",
      "postalCode": "834009",
      "addressCountry": "IN"
    },
    "areaServed": [
      "Ranchi",
      "Jharkhand"
    ],
    "medicalSpecialty": "Homeopathy",
    "founder": {
      "@type": "Physician",
      "name": "Dr. Sarita Kumari Singh",
      "jobTitle": "BHMS"
    },
    "sameAs": [
      "https://www.facebook.com/",
      "https://www.instagram.com/"
    ]
  })}
</script>
    </Helmet>

    <div className="scroll-smooth font-sans text-gray-800">
      {/* Navbar */}
      <nav className="bg-white shadow-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <img src="/images/prana-logo.png" alt="Prana Logo" className="h-10 w-10 sm:h-12 sm:w-12 object-contain" />
            <span className="text-xl sm:text-2xl font-extrabold text-emerald-600 tracking-tight">
              Prana Homoeopathic Clinic
            </span>
          </div>

          <div className="hidden md:flex space-x-6 text-base font-medium">
            {['home', 'About', 'services', 'contact'].map((section) => (
              <Link
                key={section}
                to={section}
                smooth
                duration={500}
                className="cursor-pointer text-gray-700 hover:text-emerald-600 hover:underline underline-offset-4 transition-all"
              >
                {section.charAt(0).toUpperCase() + section.slice(1)}
              </Link>
            ))}
          </div>
          <div className="md:hidden">
            <button onClick={toggleMobileMenu} className="text-emerald-600">
              {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden px-4 pb-4 space-y-2">
            {['home', 'about', 'services', 'contact'].map((section) => (
              <Link
                key={section}
                to={section}
                smooth
                duration={500}
                onClick={() => setMobileMenuOpen(false)}
                className="block text-gray-700 hover:text-emerald-600 font-medium transition"
              >
                {section.charAt(0).toUpperCase() + section.slice(1)}
              </Link>
            ))}
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section
        id="home"
        className="bg-gradient-to-br from-emerald-100 via-white to-emerald-0 py-0 sm:py-0"
      >
        <HeroSection />
      </section>

      {/* About Us */}
     <section id="about" className="py-16 sm:py-20 bg-white px-4 sm:px-8 md:px-20">
  <h2 className="text-2xl sm:text-3xl font-bold text-center text-emerald-700 mb-6 sm:mb-8">
    About Prana Homoeopathic Clinic
  </h2>

  <div className="max-w-4xl mx-auto space-y-4 sm:space-y-6 text-center text-base sm:text-lg leading-relaxed">
    <p>
      At <strong>Prana Homoeopathic Clinic</strong>, we believe in the healing
      power of nature and the body's ability to restore balance through gentle,
      holistic methods. We are committed to providing personalized homeopathic
      care for individuals and families in <strong>Ranchi, Jharkhand</strong>.
    </p>

    <p>
      Built on compassion, professionalism, and natural healing, we provide
      evidence-based homeopathic treatment in a safe and caring environment for
      patients of all age groups. Our goal is to support long-term health
      through individualized treatment plans and patient-focused care.
    </p>
  </div>
</section>

    {/* Doctor Info */}
<section id="doctor" className="py-16 sm:py-20 bg-gray-50 px-4 sm:px-8 md:px-20">

  <h2 className="text-2xl sm:text-3xl font-bold text-center text-emerald-700 mb-6 sm:mb-8">
    Meet Our Homeopathic Doctor
  </h2>

  <div className="max-w-3xl mx-auto bg-white rounded-xl shadow-md p-6 sm:p-8 text-center">

    <img
      src="/images/doctor.png"
      alt="Dr. Sarita Kumari Singh - Homeopathic Doctor in Ranchi"
      loading="lazy"
      className="w-40 h-40 sm:w-48 sm:h-48 rounded-full mx-auto mb-4 object-cover border-4 border-emerald-300 shadow-md hover:scale-105 hover:shadow-lg transition-transform duration-300"
    />

    <h3 className="text-xl sm:text-2xl font-semibold text-emerald-600 mb-2">
      Dr. Sarita Kumari Singh
    </h3>

    <p className="text-gray-700 text-base sm:text-lg">
      <strong>BHMS – Bachelor of Homoeopathic Medicine and Surgery</strong><br />
      <span className="text-gray-600">
        Graduated from BR Ambedkar University
      </span>
    </p>

    <p className="mt-4 text-gray-700 leading-relaxed text-sm sm:text-base">
      With a passion for healing and years of clinical experience,
      <strong> Dr. Sarita Kumari Singh</strong> provides compassionate,
      individualized homeopathic care for patients in
      <strong> Ranchi, Jharkhand</strong>. She believes in treating the
      root cause of illness through classical homeopathy and focuses on
      improving the overall health and well-being of every patient.
    </p>

  </div>

</section>

      {/* Services */}
     {/* Services */}
<section id="services" className="py-16 sm:py-20 bg-gray-50 px-4 sm:px-8 md:px-20">

  <h2 className="text-2xl sm:text-3xl font-bold text-center text-emerald-700 mb-4 sm:mb-6">
    Homeopathy Treatments We Offer
  </h2>

  <p className="text-center text-gray-600 max-w-3xl mx-auto mb-10">
    At <strong>Prana Homoeopathic Clinic</strong>, we provide personalized homeopathic
    treatment for acute and chronic health conditions in
    <strong> Ranchi, Jharkhand</strong>. Our holistic approach focuses on treating the
    root cause of illness while promoting long-term health and well-being.
  </p>

  <div className="grid gap-6 sm:gap-8 sm:grid-cols-2 lg:grid-cols-3">
    {[
      {
        title: 'Allergy Relief',
        description:
          'Natural homeopathic treatment for allergic rhinitis, skin allergies, sinus problems, and respiratory sensitivities.',
      },
      {
        title: 'Chronic Illness Management',
        description:
          'Personalized homeopathic treatment for asthma, arthritis, thyroid disorders, diabetes support, and other long-term health conditions.',
      },
      {
        title: 'Skin & Hair Disorders',
        description:
          'Treatment for eczema, psoriasis, acne, fungal infections, hair fall, dandruff, and other skin concerns.',
      },
      {
        title: 'Mental Health & Wellness',
        description:
          'Supportive homeopathic care for anxiety, stress, depression, emotional well-being, and sleep disorders.',
      },
      {
        title: 'Pediatrics',
        description:
          'Gentle homeopathic care for children including immunity, digestion, recurrent infections, growth, and overall wellness.',
      },
      {
        title: 'Women’s Health',
        description:
          'Homeopathic treatment for PCOS, hormonal imbalance, menstrual problems, menopause, fertility support, and women’s wellness.',
      },
    ].map((service, index) => (
      <div
        key={index}
        className="p-5 sm:p-6 bg-white rounded-xl shadow hover:shadow-md transition duration-300 border border-emerald-100"
      >
        <h3 className="text-lg sm:text-xl font-semibold text-emerald-600 mb-2">
          {service.title}
        </h3>

        <p className="text-gray-700 text-sm sm:text-base">
          {service.description}
        </p>
      </div>
    ))}
  </div>
</section>

      {/* Service Images Section */}
      <section className="py-12 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
           <img
  src="/images/image6.png"
  alt="Homeopathy consultation at Prana Homoeopathic Clinic"
  className="w-full h-80 object-cover rounded-lg shadow-md"
/>

<img
  src="/images/image5.png"
  alt="Natural homeopathic treatment at Prana Homoeopathic Clinic"
  className="w-full h-80 object-cover rounded-lg shadow-md"
/>

<img
  src="/images/image4.png"
  alt="Patient care at Prana Homoeopathic Clinic"
  className="w-full h-80 object-cover rounded-lg shadow-md"
/>
          </div>
        </div>
      </section>
      <section className="py-12 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
           <img
  src="/images/image1.png"
  alt="Homeopathy medicines and consultation"
  className="w-full h-80 object-cover rounded-lg shadow-md"
/>

<img
  src="/images/image2.png"
  alt="Prana Homoeopathic Clinic in Ranchi"
  className="w-full h-80 object-cover rounded-lg shadow-md"
/>

<img
  src="/images/image3.png"
  alt="Homeopathic treatment for chronic diseases"
  className="w-full h-80 object-cover rounded-lg shadow-md"
/>
          </div>
        </div>
      </section>

      {/* Ratings */}
      <section id="ratings" className="py-16 sm:py-20 bg-white px-4 sm:px-8 md:px-20">
        <h2 className="text-2xl sm:text-3xl font-bold text-center text-emerald-700 mb-6 sm:mb-8">Patient Ratings</h2>
        <div className="flex flex-col md:flex-row justify-center gap-6 sm:gap-8 text-center">
          <div className="bg-emerald-100 p-6 rounded-lg shadow">
            <h3 className="text-xl font-bold text-emerald-600">⭐ 4.9/5</h3>
            <p className="text-gray-600 mt-2 text-sm">Based on 300+ reviews</p>
          </div>
          <div className="bg-emerald-100 p-6 rounded-lg shadow">
            <h3 className="text-xl font-bold text-emerald-600">👩‍⚕️ Trusted Doctor</h3>
            <p className="text-gray-600 mt-2 text-sm">12+ years of experience</p>
          </div>
        </div>
      </section>
      {/* Patient Testimonials */}
{/* Patient Testimonials */}

<section
  id="testimonials"
  className="py-20 bg-gradient-to-br from-emerald-50 to-white px-4 sm:px-8 md:px-20"
>

<h2 className="text-3xl font-bold text-center text-emerald-700 mb-4">
Patient Testimonials
</h2>

<p className="text-center text-gray-600 max-w-3xl mx-auto mb-12">
Trusted by hundreds of patients across Ranchi for compassionate,
personalized and effective homeopathic treatment.
</p>

<div className="relative max-w-5xl mx-auto overflow-hidden">

<div
className="flex transition-transform duration-700 ease-in-out"
style={{
transform: `translateX(-${testimonialIndex * 100}%)`
}}
>

{[
{
name:"Mukesh Kumar",
review:"Dr. Sarita listened carefully to my concerns and provided personalized treatment. My chronic allergy has improved significantly. Highly recommended."
},
{
name:"Sunita",
review:"Very clean clinic with a caring atmosphere. The doctor explained every step of the treatment and answered all my questions patiently."
},
{
name:"Ramesh Pal",
review:"I visited for thyroid treatment. The medicines and guidance have been very helpful. Thank you for your professional care."
},
{
name:"Anjali Singh",
review:"Excellent experience. My child responded very well to the treatment. We appreciate the doctor's compassionate approach."
},
{
name:"Sudha",
review:"One of the best homeopathic clinics in Ranchi. Friendly staff, peaceful environment and effective treatment."
}
].map((item,index)=>(

<div
key={index}
className="min-w-full px-4"
>

<div className="bg-white rounded-2xl shadow-xl border border-emerald-100 p-10 text-center">

<div className="text-3xl text-yellow-500 mb-5">
★★★★★
</div>

<p className="text-gray-700 italic text-lg leading-8 min-h-[130px]">
"{item.review}"
</p>

<h3 className="mt-6 text-xl font-bold text-emerald-700">
{item.name}
</h3>

</div>

</div>

))}

</div>

</div>

<div className="flex justify-center mt-8 gap-3">

{[0,1,2,3,4].map((i)=>(

<button
key={i}
onClick={()=>setTestimonialIndex(i)}
className={`h-3 rounded-full transition-all duration-300 ${
testimonialIndex===i
? "w-8 bg-emerald-600"
: "w-3 bg-gray-300"
}`}
>

</button>

))}

</div>

</section>

      {/* FAQ */}
      <section id="faq" className="py-16 sm:py-20 bg-gray-50 px-4 sm:px-8 md:px-20">
        <h2 className="text-2xl sm:text-3xl font-bold text-center text-emerald-700 mb-6 sm:mb-8">Frequently Asked Questions</h2>
        <div className="max-w-3xl mx-auto space-y-4">
          {[
  {
    question: 'How effective is homeopathy?',
    answer:
      'Homeopathy can be effective for many acute and chronic health conditions when prescribed by a qualified homeopathic doctor. Treatment is personalized based on each patient’s symptoms and medical history.',
  },
  {
    question: 'Do you provide treatment for thyroid and PCOS?',
    answer:
      'Yes. At Prana Homoeopathic Clinic, we provide personalized homeopathic treatment for thyroid disorders, PCOS, hormonal imbalance, and other chronic health conditions.',
  },
  {
    question: 'Is homeopathy safe for children and elderly patients?',
    answer:
      'Yes. Homeopathic medicines are gentle and are commonly used for children, adults, and senior citizens under the guidance of a qualified homeopathic doctor.',
  },
  {
    question: 'How long does homeopathic treatment take?',
    answer:
      'The duration depends on the type and severity of the condition. Acute illnesses may improve within days, while chronic conditions may require a longer course of treatment.',
  },
  {
    question: 'Where is Prana Homoeopathic Clinic located?',
    answer:
      'Our clinic is located near Aruni Public School, Deepatoli, Ranchi, Jharkhand. You can also contact us on +91 9631417625 or book an appointment through WhatsApp.',
  },
  {
    question: 'How can I book an appointment?',
    answer:
      'You can call us at +91 9631417625, send us an email at homeopathyprana@gmail.com, or use the WhatsApp button on our website to book your appointment.',
  },
].map((faq, index) => (
            <div key={index} className="border border-emerald-200 rounded-lg overflow-hidden shadow-sm">
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full px-4 py-3 text-left text-base sm:text-lg font-medium text-emerald-600 bg-white hover:bg-emerald-50 focus:outline-none"
              >
                {faq.question}
              </button>
              <div
                className={`transition-all duration-300 px-4 pb-4 text-gray-700 text-sm sm:text-base ${activeFaqIndex === index ? 'block' : 'hidden'}`}
              >
                {faq.answer}
              </div>
            </div>
          ))}
        </div>
      </section>

    {/* Contact */}
<section id="contact" className="py-16 sm:py-20 bg-white px-4 sm:px-8 md:px-20">

  <h2 className="text-2xl sm:text-3xl font-bold text-center text-emerald-700 mb-4">
    Contact Prana Homoeopathic Clinic
  </h2>

  <p className="text-center text-gray-600 max-w-2xl mx-auto mb-8">
    Book your appointment today or visit our homeopathy clinic in Ranchi. We are
    committed to providing safe, natural, and personalized healthcare for you
    and your family.
  </p>

  <div className="max-w-xl mx-auto space-y-5 text-center text-sm sm:text-base">

    <p className="text-gray-700">
      📞 <strong>Phone:</strong>
      <a
        href="tel:+919631417625"
        className="text-emerald-600 hover:underline ml-1"
      >
        +91 9631417625
      </a>
    </p>

    <p className="text-gray-700">
      📧 <strong>Email:</strong>
      <a
        href="mailto:homeopathyprana@gmail.com"
        className="text-emerald-600 hover:underline ml-1"
      >
        homeopathyprana@gmail.com
      </a>
    </p>

    <p className="text-gray-700">
      📍 <strong>Address:</strong><br />
      Near Aruni Public School,<br />
      Deepatoli, Ranchi,<br />
      Jharkhand, India
    </p>

    <div className="rounded-lg overflow-hidden shadow-md mt-6">
      <iframe
        title="Prana Homoeopathic Clinic Location"
        src="https://www.google.com/maps?q=Near+Aruni+Public+School,+Deepatoli,+Ranchi,+Jharkhand&output=embed"
        width="100%"
        height="300"
        style={{ border: 0 }}
        loading="lazy"
        allowFullScreen
        referrerPolicy="no-referrer-when-downgrade"
      ></iframe>
    </div>

    <a
      href="https://wa.me/919631417625?text=Hello%20Prana%20Homoeopathic%20Clinic%2C%20I%20would%20like%20to%20book%20an%20appointment."
      target="_blank"
      rel="noopener noreferrer"
      className="inline-block mt-6 px-6 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition"
    >
      Chat on WhatsApp
    </a>

  </div>

</section>

      {/* Footer */}
      <footer className="bg-emerald-600 text-white py-6 text-sm sm:text-base">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-center px-4 space-y-2 md:space-y-0">
          <p>© {new Date().getFullYear()} Prana Homoeopathic Clinic. All rights reserved.</p>
          <div className="flex space-x-4">
           <button className="hover:underline">Facebook</button>
<button className="hover:underline">Instagram</button>
<button className="hover:underline">YouTube</button>
          </div>
        </div>
      </footer>
     </div>
  </>
  );
}

export default App;
